/**
 * TierEditDialog — edit a capability tier.
 *
 * ── WHAT CHANGED, AND WHY (2026-08-03 admin wiring audit, F1 then A-F1) ──────
 * This dialog used to edit all eight columns of quill_capability_tiers. Five of
 * them were never read by quill-engine, so F1 removed their editors and made
 * the backend answer 410. FOUR of those five are still dead and still refused:
 *
 *   classifier_model                 the engine annotates both DEPRECATED: the
 *   classifier_temperature           classifier's model comes from the
 *                                    guardrails_classifier pipeline row and its
 *                                    temperature from the
 *                                    constitutional_classifier_prompt config row.
 *   decision_log_enabled             guardrails/log/index.js reads only
 *                                    tier.tier_id — logging is unconditional.
 *   available_to_subscription_tiers  zero engine references.
 *
 * capability_categories is the one that came back. F1 recorded the wiring as a
 * product decision for Grant precisely because making a tier's category list
 * constrain the classifier changes live refusal behaviour for real members.
 * Grant took it: quill-engine's llmClassifier now appends the resolved tier's
 * categories to the constitutional classifier's system prompt
 * (buildClassifierSystemPrompt), so the column is READ and the editor is real.
 *
 * WHAT AN OPERATOR MUST UNDERSTAND ABOUT THIS FIELD, and why the note below
 * says it in those words:
 *
 *   It only ADDS. The list grants explicit allowances on top of the
 *   admin-authored classifier prompt. Removing a name withdraws that
 *   allowance and hands the decision back to the base prompt — which is what
 *   "lock this member down" has to mean — but no list can make the classifier
 *   refuse something the base prompt already treats as on-mission. Anything
 *   about the member's own life, and anything emotional or vulnerable, stays
 *   on-mission whatever is in here. That asymmetry is deliberate: the failure
 *   we must never ship is a misconfiguration that refuses a bereaved member
 *   mid-story.
 *
 *   An empty list is legal and means "no explicit allowances" — the engine
 *   sends the prompt it sent before this wiring existed.
 *
 *   A malformed name is refused at save (400), not stored, because the engine
 *   drops a whole list containing one — a stored-but-ignored list would be
 *   this dialog saying "saved" and changing nothing all over again.
 *
 * PATCH /guardrails/tiers/:tierId therefore accepts exactly: tier_name,
 * tier_description, capability_categories, fast_path_enabled, is_active.
 * member_count is read-only.
 */

import React, { useMemo, useState } from 'react';
import {
  Box,
  TextField,
  Typography,
  Switch,
  FormControlLabel,
  Alert,
  AlertTitle,
  Autocomplete,
  Chip,
  Divider,
} from '@mui/material';
import AdminEditDialogShell from '../../shared/AdminEditDialogShell';
import QuillActionConfirm from '../common/QuillActionConfirm';
import { colors } from '../../../theme/designTokens';
import { CapabilityTier, TierPatch } from '../../../services/quillAdminService';
import { categoriesToArray } from './guardrailUi';

interface TierEditDialogProps {
  tier: CapabilityTier;
  open: boolean;
  saving: boolean;
  onClose: () => void;
  onSave: (patch: TierPatch) => Promise<void> | void;
  /**
   * Known capability categories offered as suggestions (the union of every
   * tier's list). Free-solo entry still allows a genuinely new one.
   */
  knownCategories?: string[];
}

const ACCENT = colors.deepTeal[500];

/**
 * MUST stay identical to quill-engine's normalizeCapabilityCategories and to
 * quill-admin-backend's capabilityCategoriesProblem. Validating here only buys
 * the operator a faster, clearer answer than the API's 400 — it is not the
 * gate, and it must never be more permissive than the gate.
 */
const CAPABILITY_TOKEN = /^[a-z0-9][a-z0-9_]{0,63}$/i;
const MAX_CAPABILITY_CATEGORIES = 40;

export function capabilityCategoriesProblem(values: string[]): string | null {
  if (values.length > MAX_CAPABILITY_CATEGORIES) {
    return `At most ${MAX_CAPABILITY_CATEGORIES} categories (currently ${values.length}).`;
  }
  for (const value of values) {
    if (value === '*') {
      if (values.length !== 1) return 'The “*” wildcard must be the only entry.';
      continue;
    }
    if (!CAPABILITY_TOKEN.test(value)) {
      return `“${value}” is not a category name — lower-case letters, digits and underscores only. `
        + 'quill-engine ignores a whole list containing anything else.';
    }
  }
  return null;
}

function CategoryAutocomplete({
  values,
  options,
  onChange,
  error,
}: {
  values: string[];
  options: string[];
  onChange: (next: string[]) => void;
  error: string | null;
}) {
  const suggestions = options.filter((o) => !values.includes(o));
  return (
    <Autocomplete
      multiple
      freeSolo
      options={suggestions}
      value={values}
      onChange={(_e, next) => {
        const cleaned = Array.from(
          new Set((next as string[]).map((v) => v.trim()).filter(Boolean))
        );
        onChange(cleaned);
      }}
      renderTags={(tagValues, getTagProps) =>
        tagValues.map((option, index) => (
          <Chip label={option} size="small" {...getTagProps({ index })} key={option} />
        ))
      }
      renderInput={(params) => (
        <TextField
          {...params}
          size="small"
          error={!!error}
          placeholder="Add a capability category"
          helperText={
            error
            || 'Pick a known category or type a new one, then press Enter. Empty means “no explicit allowances”.'
          }
        />
      )}
      sx={{ mb: 1 }}
    />
  );
}

const TierEditDialog: React.FC<TierEditDialogProps> = ({
  tier,
  open,
  saving,
  onClose,
  onSave,
  knownCategories = [],
}) => {
  const [tierName, setTierName] = useState(tier.tier_name);
  const [tierDescription, setTierDescription] = useState(tier.tier_description ?? '');
  const [categories, setCategories] = useState<string[]>(categoriesToArray(tier.capability_categories));
  const [fastPathEnabled, setFastPathEnabled] = useState(tier.fast_path_enabled);
  const [isActive, setIsActive] = useState(tier.is_active);
  const [confirmOpen, setConfirmOpen] = useState(false);

  const categoriesError = capabilityCategoriesProblem(categories);

  const initialSnapshot = useMemo(
    () =>
      JSON.stringify({
        tierName: tier.tier_name,
        tierDescription: tier.tier_description ?? '',
        categories: categoriesToArray(tier.capability_categories),
        fastPathEnabled: tier.fast_path_enabled,
        isActive: tier.is_active,
      }),
    [tier]
  );

  const currentSnapshot = JSON.stringify({
    tierName,
    tierDescription,
    categories,
    fastPathEnabled,
    isActive,
  });

  const isDirty = initialSnapshot !== currentSnapshot;

  // Exactly the five fields the backend accepts. Sending any of the four
  // retired ones would be answered 410 and write nothing.
  const buildPatch = (): TierPatch => ({
    tier_name: tierName,
    tier_description: tierDescription || null,
    capability_categories: categories,
    fast_path_enabled: fastPathEnabled,
    is_active: isActive,
  });

  const handleConfirm = async () => {
    await onSave(buildPatch());
    setConfirmOpen(false);
  };

  return (
    <>
      <AdminEditDialogShell
        open={open}
        onClose={onClose}
        accentColor={ACCENT}
        typeInitial={tier.tier_name.charAt(0).toUpperCase()}
        title={`Edit ${tier.tier_name}`}
        subtitle={`${tier.tier_id} • ${tier.member_count.toLocaleString()} members`}
        onSave={() => { if (!categoriesError) setConfirmOpen(true); }}
        saveLabel="Save changes"
        isSaving={saving}
        isDirty={isDirty && !categoriesError}
        maxWidth="md"
      >
        <Box sx={{ p: 3 }} data-testid="tier-edit-dialog">
          <Alert severity="warning" sx={{ mb: 3 }}>
            Everything below is live — it changes real member conversations as soon as it's
            saved.
          </Alert>

          <TextField
            label="Tier name"
            fullWidth
            size="small"
            value={tierName}
            onChange={(e) => setTierName(e.target.value)}
            sx={{ mb: 2 }}
          />
          <TextField
            label="Description"
            fullWidth
            multiline
            minRows={2}
            size="small"
            value={tierDescription}
            onChange={(e) => setTierDescription(e.target.value)}
            sx={{ mb: 3 }}
          />

          <Divider sx={{ mb: 2 }} />

          <Typography sx={{ fontWeight: 600, mb: 0.5 }}>Capability categories</Typography>
          <Typography variant="body2" sx={{ color: 'text.secondary', mb: 1.5 }}>
            Appended to the scope classifier's prompt as things a member on this tier is
            explicitly allowed to ask for.
          </Typography>
          <CategoryAutocomplete
            values={categories}
            options={knownCategories}
            onChange={setCategories}
            error={categoriesError}
          />

          <Divider sx={{ my: 2 }} />

          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5, mb: 3 }}>
            <FormControlLabel
              control={
                <Switch
                  checked={fastPathEnabled}
                  onChange={(e) => setFastPathEnabled(e.target.checked)}
                  slotProps={{ input: { 'aria-label': 'Fast-path keyword matching enabled' } }}
                />
              }
              label="Fast-path keyword/regex matching enabled"
            />
            <FormControlLabel
              control={
                <Switch
                  checked={isActive}
                  onChange={(e) => setIsActive(e.target.checked)}
                  slotProps={{ input: { 'aria-label': 'Tier is active' } }}
                />
              }
              label="Tier is active"
            />
          </Box>

          {/* The honest note. It has to do two jobs now: say what the category
              list DOES (and, just as importantly, what it cannot do), and name
              where the rest of the classifier's behaviour actually comes from,
              because four editors used to sit here implying it came from the
              tier. */}
          <Alert severity="info" data-testid="tier-retired-fields-note">
            <AlertTitle>What this tier does, and what it doesn't</AlertTitle>
            <Typography variant="body2" sx={{ mb: 1 }}>
              The capability categories above are added to the scope classifier's prompt as an
              explicit allowance for members on this tier. <strong>The list only adds.</strong>{' '}
              Removing a category withdraws its allowance and hands that decision back to the
              classifier prompt — it can never make the classifier refuse something the prompt
              already treats as on-mission. A member's own life, and anything emotional or
              vulnerable, stays on-mission whatever is in this list. An empty list is fine: it
              means no explicit allowances, which is exactly how the classifier behaved before
              this field was wired up.
            </Typography>
            <Typography variant="body2" sx={{ mb: 1 }}>
              The tier also decides whether the keyword fast path runs before the classifier, and
              whether the tier is active at all. It carries no model or temperature that the
              engine reads.
            </Typography>
            <Typography variant="body2">
              The classifier's prompt, temperature and token budget live on the{' '}
              <strong>Reasoning Prompts</strong> page (
              <code>constitutional_classifier_prompt</code>), and the model it runs on lives on the{' '}
              <strong>Pipeline Models</strong> page (<code>guardrails_classifier</code>).
            </Typography>
          </Alert>
        </Box>
      </AdminEditDialogShell>

      <QuillActionConfirm
        open={confirmOpen}
        title="Save tier changes"
        message={`This changes live classifier behavior for ${tier.member_count.toLocaleString()} members immediately (production).`}
        confirmLabel="Confirm & Save"
        loading={saving}
        pendingLabel="Applying…"
        onConfirm={handleConfirm}
        onCancel={() => setConfirmOpen(false)}
      />
    </>
  );
};

export default TierEditDialog;
